import type { MantineColorsTuple } from '@mantine/core';
import { storage } from '@newo/storage';
import {
	CreatorAgentSuccessFlowStepKey as AgentSuccessStep,
	CreatorAgentSuccessFlowVariant,
	CreatorAgentSuccessRedirectTarget,
	type CreatorVideoMapKey,
	type TenantMetadata,
	tenantMetadata
} from '@newo/tenant-metadata';
import { getTheme } from '@newo/theme';
import {
	Calendar,
	LayoutDashboard,
	PhoneForwarded,
	PhoneIncoming
} from 'lucide-react';
import { runtimeConfig } from '@/constants';
import {
	AgentSuccessAction,
	CardType,
	OnboardingTour,
	type TAgentSuccessBaseCard,
	type TAgentSuccessConfig,
	type TAgentSuccessStepCard
} from '@/types/partner-metadata.types.ts';
import { CREATOR_VIDEO_MAP_KEYS } from '@/utils/creatorVideoKeys';

// TODO: temporary override — remove once CMS serves this URL in creator_settings.videos.
const HARDCODED_VIDEO_SOURCE =
	'https://storage.googleapis.com/newo-static-files/video/Newo_video_v2.mp4';
const HARDCODED_VIDEO_SOURCE_KEYS: CreatorVideoMapKey[] = [
	'intro',
	'training_waiting',
	'activate_account'
];

export class PartnerMetadata {
	private initialized = false;
	private themeSteps = [
		50, 100, 200, 300, 400, 500, 600, 700, 800, 900
	] as const;
	private readonly deferred = Promise.withResolvers<void>();

	public initialize(): Promise<void> {
		if (!this.initialized) {
			this.initialized = true;
			void this.bootstrap();
		}

		return this.deferred.promise;
	}

	private applyPrimaryThemeColors(colors: MantineColorsTuple) {
		const root = document.documentElement;

		this.themeSteps.forEach((step, index) => {
			root.style.setProperty(`--color-primary-${step}`, colors[index]);
		});
	}

	private async bootstrap(): Promise<void> {
		try {
			await tenantMetadata.init({
				partnerIdn: runtimeConfig.partnerIdn,
				cmsApiUrl: runtimeConfig.cmsApiUrl || undefined,
				storage
			});

			const theme = getTheme(this.metadata.theme);

			if (theme.colors?.primary) {
				this.applyPrimaryThemeColors(theme.colors.primary);
			}

			this.deferred.resolve();
		} catch (error) {
			this.deferred.reject(error);
		}
	}

	private get metadata(): TenantMetadata {
		return tenantMetadata.metadata;
	}

	private getBranding(): TenantMetadata['branding'] {
		return this.metadata.branding;
	}

	private getFeatureFlags(): TenantMetadata['feature_flags'] {
		return this.metadata.feature_flags;
	}

	private getCreatorSettings(): TenantMetadata['creator_settings'] {
		return this.metadata.creator_settings;
	}

	private getAgentSuccessFlow(): TenantMetadata['creator_settings']['agent_success_flow'] {
		return this.getCreatorSettings().agent_success_flow;
	}

	private getAgentSuccessBaseCards(): Record<
		AgentSuccessStep,
		TAgentSuccessBaseCard
	> {
		return {
			[AgentSuccessStep.ActivateAccount]: {
				videoSrc: this.getCreatorVideoSource(
					CREATOR_VIDEO_MAP_KEYS.ACCOUNT_ACTIVATION
				),
				// TODO: the info hint is hidden on this card — restore `showInfo: true` to
				// bring the button and its modal back.
				action: AgentSuccessAction.ACTIVATE_ACCOUNT
			},

			[AgentSuccessStep.ScheduleCall]: {
				icon: Calendar,
				action: AgentSuccessAction.REDIRECT
			},

			[AgentSuccessStep.GetChatbot]: {
				type: CardType.SCRIPT,
				action: AgentSuccessAction.REDIRECT
			},

			[AgentSuccessStep.StartReceivingCalls]: {
				icon: PhoneIncoming,
				action: AgentSuccessAction.REDIRECT
			},

			[AgentSuccessStep.StartMakingCalls]: {
				icon: PhoneForwarded,
				action: AgentSuccessAction.REDIRECT
			},

			[AgentSuccessStep.AccessDashboard]: {
				action: AgentSuccessAction.REDIRECT,
				icon: LayoutDashboard
			}
		};
	}

	public getCreatorVideoSource(key: CreatorVideoMapKey): string | undefined {
		if (HARDCODED_VIDEO_SOURCE_KEYS.includes(key)) {
			return HARDCODED_VIDEO_SOURCE;
		}

		const videos = this.creatorVideos;

		if (!videos) {
			return;
		}

		return Object.fromEntries(videos.map((video) => [video.key, video.url]))[
			key
		];
	}

	public get creatorVideos(): TenantMetadata['creator_settings']['videos'] {
		return this.getCreatorSettings().videos;
	}

	public getId(): string {
		return this.metadata.id;
	}

	public getName(): string {
		return this.metadata.name;
	}

	public getTheme() {
		return this.metadata.theme ?? null;
	}

	public getSubmitCtaRadius(): number {
		if (this.metadata.id === 'ionos') {
			return 100;
		}

		return 12;
	}

	public isAlwaysFilledStepButton(): boolean {
		return this.metadata.id === 'ionos';
	}

	public isCallViaDeviceHidden(): boolean {
		return this.metadata.id === 'ionos';
	}

	public getIsAcronisVariant() {
		return this.metadata.id === 'acronis';
	}

	public getDarkLogoUrl(preferSvg = false): string {
		const logo = this.getBranding().logo.dark;

		return preferSvg ? logo.svg : logo.png;
	}

	public getFaviconUrl(): string {
		return this.getBranding().favicon_url;
	}

	public isLocalizationEnabled(): boolean {
		return this.getFeatureFlags().is_localization_enabled;
	}

	public isCreatorChatFlowEnabled(): boolean {
		return this.getFeatureFlags().is_creator_chat_flow_enabled;
	}

	public isVibeCreatorConfirmationFlowEnabled(): boolean {
		return this.getFeatureFlags().is_vibe_creator_confirmation_flow_enabled;
	}

	public isVibeCreatorFlowEnabled(): boolean {
		return this.getFeatureFlags().is_vibe_creator_enabled;
	}

	public isVideosHidden(): boolean {
		return this.getFeatureFlags().is_videos_hidden;
	}

	public isCreatorTrainingBackButtonHidden(): boolean {
		return this.getFeatureFlags().is_creator_training_back_button_hidden;
	}

	public getCheckoutSuccessUrl(): string | null {
		return this.getCreatorSettings().checkout_success_return_url;
	}

	public isSeparateActivateButtonEnabled(): boolean {
		return this.getFeatureFlags().creator_agent_success
			.is_separate_activate_button_visible;
	}

	public isExternalAccountNumberEnabled(): boolean {
		return this.getFeatureFlags().customer_details
			.is_external_account_number_enabled;
	}

	public isStepsBlockHidden(): boolean {
		return this.getFeatureFlags().creator_agent_success.is_steps_block_hidden;
	}

	public isPortalLoginHintEnabled(): boolean {
		return (
			this.getFeatureFlags().creator_agent_success
				.is_portal_login_hint_enabled ?? false
		);
	}

	public isCreatorAgentConfigurationRedirectEnabled(): boolean {
		return (
			this.getFeatureFlags().is_creator_agent_configuration_redirect_enabled ??
			false
		);
	}

	public getCheckoutProductKey(): string {
		return this.getCreatorSettings().success_page.checkout_product_key;
	}

	public getCheckoutPromotionCode(): string | null {
		return this.getCreatorSettings().success_page.promotion_code ?? null;
	}

	public getAgentSuccessVariant(): CreatorAgentSuccessFlowVariant {
		return (
			this.getAgentSuccessFlow()?.variant ??
			CreatorAgentSuccessFlowVariant.Default
		);
	}

	public isSpecificCreatorEnabled(): boolean | undefined {
		return this.getFeatureFlags().is_specific_creator_enabled;
	}

	public isAgentSuccessCollapseVariant(): boolean {
		return (
			this.getAgentSuccessVariant() === CreatorAgentSuccessFlowVariant.Collapse
		);
	}

	public getAgentSuccessConfig(): TAgentSuccessConfig {
		const agentSuccessFlow = this.getAgentSuccessFlow();
		const baseCards = this.getAgentSuccessBaseCards();
		const variant = this.getAgentSuccessVariant();
		const stepOrder = agentSuccessFlow?.steps.map((step) => step.key) ?? [];
		const steps = stepOrder.reduce(
			// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Todo remove after migrating to vibe creator
			(acc, stepKey, index) => {
				const step = agentSuccessFlow?.steps[index];
				const baseCard = baseCards[stepKey];

				if (!baseCard || !step) {
					return acc;
				}

				const card: TAgentSuccessStepCard = {
					...baseCard,
					...(step.next_step ? { nextStep: step.next_step } : {}),
					...(step.url && stepKey !== AgentSuccessStep.AccessDashboard
						? { url: step.url }
						: {})
				};

				if (stepKey === AgentSuccessStep.AccessDashboard) {
					if (this.isCreatorAgentConfigurationRedirectEnabled()) {
						card.portalRedirectTarget =
							CreatorAgentSuccessRedirectTarget.AgentConfiguration;
					} else {
						card.portalRedirectTarget =
							CreatorAgentSuccessRedirectTarget.Dashboard;
						card.onboardingTour = OnboardingTour.PORTAL;
					}
				}

				if (
					variant === CreatorAgentSuccessFlowVariant.Collapse &&
					stepKey !== AgentSuccessStep.AccessDashboard
				) {
					card.action = AgentSuccessAction.NEXT_CARD;

					if (step.url) {
						card.learnMoreLink = step.url;
					}
				}

				acc[stepKey] = card;

				return acc;
			},
			{} as TAgentSuccessConfig['steps']
		);

		return {
			variant,
			isStepperIncluded: agentSuccessFlow?.is_stepper_included ?? false,
			stepOrder,
			steps
		};
	}

	public getTermsOfServicesUrl(): string {
		return this.getCreatorSettings().terms_of_services_url ?? '';
	}
	public isItalianLocalizationDisabled(): boolean {
		return this.getFeatureFlags().is_italian_localization_disabled;
	}
}

export const partnerMetadata = new PartnerMetadata();
